import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './MangaDetailModal.module.css';
import { libraryApi, wishlistApi, shopsApi, getMediaUrl } from '../../api';
import { useConfirm } from '../../context/ConfirmContext';

// Sprachcode (z.B. "de", "en") in einen lesbaren Namen in der aktuellen UI-Sprache
// umwandeln - nutzt die Browser-eigene Intl-API statt eigener Übersetzungstabelle,
// damit auch Codes funktionieren, die hier nicht explizit gepflegt werden.
function formatLanguage(code, uiLanguage) {
  try {
    return new Intl.DisplayNames([uiLanguage], { type: 'language' }).of(code) || code;
  } catch (_) {
    return code;
  }
}

function MangaDetailModal({ isOpen, onClose, mangaId, userId, coverUrl, mangaTitle, onMangaDeleted, onBandDeleted }) {
  const { t, i18n } = useTranslation();
  const confirm = useConfirm();
  const [mangaInfo, setMangaInfo] = useState(null);
  const [userBands, setUserBands] = useState([]);
  const [allBands, setAllBands] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [progress, setProgress] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [bandMsg, setBandMsg] = useState(null); // Inline-Feedback für Bands
  const [wishlistItems, setWishlistItems] = useState([]); // Einträge dieses Mangas
  const [notifSettings, setNotifSettings] = useState(null);
  const [notifSaving, setNotifSaving] = useState(false);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [shopPanels, setShopPanels] = useState({}); // bandId -> { open, loading, loaded, data, error }
  const [bulkActionLoading, setBulkActionLoading] = useState(null); // 'collection' | 'wishlist' | 'read' | null
  const [bulkMsg, setBulkMsg] = useState(null);
  const [expandedBandNumber, setExpandedBandNumber] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isOpen || !mangaId) {
      return;
    }

    const fetchMangaDetails = async () => {
      try {
        setIsLoading(true);
        setError(null);


        // Parallel laden: Manga-Info, Bände, Progress, Wishlist-Status, Benachrichtigungseinstellungen,
        // globale Band-Metadaten (Autor/Verlag/Erscheinungsdatum - nicht in der Library-Antwort enthalten)
        const [infoResult, bandsResult, progressResult, wishlistResult, notifResult, globalBandsResult] = await Promise.all([
          libraryApi.getMangaById(mangaId),
          libraryApi.getAllBands(mangaId),
          libraryApi.getMangaProgress(mangaId),
          wishlistApi.getWishlist(),
          libraryApi.getMangaNotificationSettings(mangaId),
          libraryApi.getGlobalBands(mangaId)
        ]);

        const globalBandByNumber = {};
        if (globalBandsResult.success) {
          (globalBandsResult.data?.items || []).forEach(b => { globalBandByNumber[b.band_number] = b; });
        }

        if (wishlistResult.success) {
          setWishlistItems((wishlistResult.data || []).filter(item => item.manga_id === mangaId));
        }

        if (notifResult.success && notifResult.data) {
          setNotifSettings(notifResult.data);
        }

        // Manga Info verarbeiten
        if (infoResult.success && infoResult.data) {
          setMangaInfo(infoResult.data);
        }

        // Backend liefert jetzt ALLE Bände mit owned-Flag + band_id
        const allItems = bandsResult.success ? (bandsResult.items || []) : [];

        // userBands = nur die besessenen (für Kompatibilität mit anderen Funktionen)
        const ownedBands = allItems.filter(b => b.owned);
        setUserBands(ownedBands);

        // Progress verarbeiten
        if (progressResult.success && progressResult.data) {
          setProgress(progressResult.data);
        }

        // allBands direkt aus dem Response aufbauen – kein synthetisches Loop mehr nötig
        const allBandsList = allItems
          .sort((a, b) => a.band_number - b.band_number)
          .map(b => {
            const globalBand = globalBandByNumber[b.band_number];
            return {
              bandNumber: b.band_number,
              isOwned: !!b.owned,
              bandId: b.band_id,
              titel: b.band_title || `${infoResult.data?.title || mangaTitle} ${String(b.band_number).padStart(2, '0')}`,
              readingStatus: b.reading_status || 'UNREAD',
              coverUrl: (b.cover_media_key || globalBand?.cover_media_key) ? getMediaUrl(b.cover_media_key || globalBand?.cover_media_key) : null,
              author: globalBand?.author || null,
              publisher: globalBand?.publisher || null,
              publishedDate: globalBand?.published_date || null,
              language: globalBand?.language || null,
            };
          });

        setAllBands(allBandsList);
        setIsLoading(false);

      } catch (err) {
        console.error('❌ Fehler beim Laden der Manga Details:', err);
        setError(err.message);
        setIsLoading(false);
      }
    };

    fetchMangaDetails();
  }, [isOpen, mangaId, mangaTitle]);

  if (!isOpen) return null;

  const handleDeleteBand = async (band) => {
    if (!band.isOwned || !band.bandId) return;
    
    const confirmDelete = await confirm(t('mangaDetail.confirmRemoveBand', { number: band.bandNumber }), { danger: true });
    if (!confirmDelete) return;

    setIsDeleting(true);
    try {
      const result = await libraryApi.removeBand(band.bandId);

      if (result.success) {
        setUserBands(prev => prev.filter(b => b.band_id !== band.bandId));
        setAllBands(prev => prev.map(b => 
          b.bandNumber === band.bandNumber 
            ? { ...b, isOwned: false }  // bandId bleibt, da globale ID
            : b
        ));
        if (onBandDeleted) onBandDeleted(band);
      } else {
        setError(t('mangaDetail.errorRemovingBand', { message: result.error?.message || t('mangaDetail.unknownError') }));
      }
    } catch (error) {
      setError(t('mangaDetail.networkErrorRemovingBand'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddBand = async (band) => {
    if (band.isOwned) return;
    setBandMsg(null);
    setIsDeleting(true);
    try {
      const result = await libraryApi.addBandByNumber(mangaId, band.bandNumber);
      if (result.success) {
        const d = result.data;
        setUserBands(prev => [...prev, {
          band_id: d.band_id,
          band_number: d.band_number,
          band_title: d.band_title,
          cover_media_key: d.cover_media_key,
          reading_status: d.reading_status
        }]);
        setAllBands(prev => prev.map(b =>
          b.bandNumber === band.bandNumber
            ? { ...b, isOwned: true, bandId: d.band_id, titel: d.band_title, coverUrl: getMediaUrl(d.cover_media_key) }
            : b
        ));
        setBandMsg({ type: 'success', text: t('mangaDetail.bandAdded', { number: band.bandNumber }), bandNumber: band.bandNumber });
      } else {
        const code = result.error?.code;
        if (code === 'ALREADY_OWNED' || code === 'CONFLICT') {
          setBandMsg({ type: 'info', text: t('mangaDetail.bandAlreadyOwned', { number: band.bandNumber }), bandNumber: band.bandNumber });
        } else if (code === 'NOT_FOUND') {
          setBandMsg({ type: 'warning', text: t('mangaDetail.bandNotInDb', { number: band.bandNumber }), bandNumber: band.bandNumber, showNav: true });
        } else {
          setBandMsg({ type: 'error', text: result.error?.message || t('mangaDetail.errorAdding'), bandNumber: band.bandNumber });
        }
      }
    } catch (e) {
      setBandMsg({ type: 'error', text: e.message, bandNumber: band.bandNumber });
    } finally {
      setIsDeleting(false);
      setTimeout(() => setBandMsg(null), 5000);
    }
  };

  const findWishlistEntry = (band) => {
    return wishlistItems.find(item => item.band_id != null && item.band_id === band.bandId) || null;
  };

  const handleChangeReadingStatus = async (band, newStatus) => {
    if (!band.isOwned || !band.bandId || newStatus === band.readingStatus) return;

    const previousStatus = band.readingStatus;
    setAllBands(prev => prev.map(b =>
      b.bandNumber === band.bandNumber ? { ...b, readingStatus: newStatus } : b
    ));

    try {
      const result = await libraryApi.updateBandStatus(band.bandId, newStatus);
      if (!result.success) {
        setAllBands(prev => prev.map(b =>
          b.bandNumber === band.bandNumber ? { ...b, readingStatus: previousStatus } : b
        ));
        setBandMsg({ type: 'error', text: result.error?.message || t('mangaDetail.errorUpdatingReadingStatus'), bandNumber: band.bandNumber });
        setTimeout(() => setBandMsg(null), 5000);
      }
    } catch (e) {
      setAllBands(prev => prev.map(b =>
        b.bandNumber === band.bandNumber ? { ...b, readingStatus: previousStatus } : b
      ));
      setBandMsg({ type: 'error', text: e.message, bandNumber: band.bandNumber });
      setTimeout(() => setBandMsg(null), 5000);
    }
  };

  const handleWishlistBand = async (band) => {
    setBandMsg(null);

    // Bereits auf der Wunschliste -> entfernen (Toggle)
    const existing = findWishlistEntry(band);
    if (existing) {
      try {
        const result = await wishlistApi.removeItem(existing.wishlist_id);
        if (result.success) {
          setWishlistItems(prev => prev.filter(item => item.wishlist_id !== existing.wishlist_id));
          setBandMsg({ type: 'info', text: t('mangaDetail.removedFromWishlist', { number: band.bandNumber }), bandNumber: band.bandNumber });
        } else {
          setBandMsg({ type: 'error', text: result.error?.message || t('mangaDetail.errorRemoving'), bandNumber: band.bandNumber });
        }
      } catch (e) {
        setBandMsg({ type: 'error', text: e.message, bandNumber: band.bandNumber });
      }
      setTimeout(() => setBandMsg(null), 5000);
      return;
    }

    try {
      let result;

      if (band.bandId != null) {
        // Beste Option: band_id direkt senden
        console.debug('[Wishlist] addBand', { manga_id: mangaId, band_id: band.bandId });
        result = await wishlistApi.addBand(mangaId, band.bandId);
      } else if (band.bandNumber != null) {
        // Fallback Option B: band_number senden
        console.debug('[Wishlist] addBandByNumber', { manga_id: mangaId, band_number: band.bandNumber });
        result = await wishlistApi.addBandByNumber(mangaId, band.bandNumber);
      } else {
        // Letzter Fallback: manga-only
        console.debug('[Wishlist] addManga', { manga_id: mangaId });
        result = await wishlistApi.addManga(mangaId);
      }

      if (result.success) {
        const label = band.bandId != null || band.bandNumber != null
          ? t('mangaDetail.bandAddedToWishlist', { number: band.bandNumber })
          : t('mangaDetail.mangaAddedToWishlist');
        setBandMsg({ type: 'success', text: `♥ ${label}`, bandNumber: band.bandNumber });
        setWishlistItems(prev => [...prev, { manga_id: mangaId, band_id: band.bandId ?? null, band_number: band.bandNumber ?? null }]);
      } else {
        const code = result.error?.code;
        if (code === 'ALREADY_EXISTS' || code === 'CONFLICT') {
          setBandMsg({ type: 'info', text: t('mangaDetail.alreadyOnWishlist', { number: band.bandNumber }), bandNumber: band.bandNumber });
        } else {
          setBandMsg({ type: 'error', text: result.error?.message || t('mangaDetail.errorAdding'), bandNumber: band.bandNumber });
        }
      }
    } catch (e) {
      setBandMsg({ type: 'error', text: e.message, bandNumber: band.bandNumber });
    }
    setTimeout(() => setBandMsg(null), 5000);
  };

  const handleBulkAddToCollection = async () => {
    const missing = allBands.filter(b => !b.isOwned);
    if (missing.length === 0 || bulkActionLoading) return;

    setBulkActionLoading('collection');
    setBulkMsg(null);
    let added = 0;
    let failed = 0;

    for (const band of missing) {
      try {
        const result = await libraryApi.addBandByNumber(mangaId, band.bandNumber);
        if (result.success) {
          const d = result.data;
          added += 1;
          setUserBands(prev => [...prev, {
            band_id: d.band_id,
            band_number: d.band_number,
            band_title: d.band_title,
            cover_media_key: d.cover_media_key,
            reading_status: d.reading_status
          }]);
          setAllBands(prev => prev.map(b =>
            b.bandNumber === band.bandNumber
              ? { ...b, isOwned: true, bandId: d.band_id, titel: d.band_title, coverUrl: getMediaUrl(d.cover_media_key) }
              : b
          ));
        } else {
          failed += 1;
        }
      } catch (e) {
        failed += 1;
      }
    }

    setBulkActionLoading(null);
    setBulkMsg({
      type: failed === 0 ? 'success' : (added > 0 ? 'info' : 'error'),
      text: failed === 0
        ? t('mangaDetail.bulkAddCollectionSuccess', { count: added })
        : t('mangaDetail.bulkAddCollectionPartial', { added, failed })
    });
    setTimeout(() => setBulkMsg(null), 6000);
  };

  const handleBulkAddToWishlist = async () => {
    const missing = allBands.filter(b => !b.isOwned && !findWishlistEntry(b));
    if (missing.length === 0 || bulkActionLoading) return;

    setBulkActionLoading('wishlist');
    setBulkMsg(null);
    let added = 0;
    let failed = 0;

    for (const band of missing) {
      try {
        let result;
        if (band.bandId != null) {
          result = await wishlistApi.addBand(mangaId, band.bandId);
        } else {
          result = await wishlistApi.addBandByNumber(mangaId, band.bandNumber);
        }
        if (result.success) {
          added += 1;
          setWishlistItems(prev => [...prev, { manga_id: mangaId, band_id: band.bandId ?? null, band_number: band.bandNumber ?? null }]);
        } else {
          const code = result.error?.code;
          if (code === 'ALREADY_EXISTS' || code === 'CONFLICT') {
            added += 1;
          } else {
            failed += 1;
          }
        }
      } catch (e) {
        failed += 1;
      }
    }

    setBulkActionLoading(null);
    setBulkMsg({
      type: failed === 0 ? 'success' : (added > 0 ? 'info' : 'error'),
      text: failed === 0
        ? t('mangaDetail.bulkAddWishlistSuccess', { count: added })
        : t('mangaDetail.bulkAddWishlistPartial', { added, failed })
    });
    setTimeout(() => setBulkMsg(null), 6000);
  };

  const handleBulkMarkAllRead = async () => {
    const unread = allBands.filter(b => b.isOwned && b.bandId && b.readingStatus !== 'READ');
    if (unread.length === 0 || bulkActionLoading) return;

    setBulkActionLoading('read');
    setBulkMsg(null);
    let updated = 0;
    let failed = 0;

    for (const band of unread) {
      try {
        const result = await libraryApi.updateBandStatus(band.bandId, 'READ');
        if (result.success) {
          updated += 1;
          setAllBands(prev => prev.map(b =>
            b.bandNumber === band.bandNumber ? { ...b, readingStatus: 'READ' } : b
          ));
        } else {
          failed += 1;
        }
      } catch (e) {
        failed += 1;
      }
    }

    setBulkActionLoading(null);
    setBulkMsg({
      type: failed === 0 ? 'success' : (updated > 0 ? 'info' : 'error'),
      text: failed === 0
        ? t('mangaDetail.bulkMarkReadSuccess', { count: updated })
        : t('mangaDetail.bulkMarkReadPartial', { updated, failed })
    });
    setTimeout(() => setBulkMsg(null), 6000);
  };

  const toggleBandInfo = (bandNumber) => {
    setExpandedBandNumber(prev => (prev === bandNumber ? null : bandNumber));
  };

  const handleToggleShopLinks = async (band) => {
    const bandId = band.bandId;
    if (!bandId) return;

    const existing = shopPanels[bandId];
    setShopPanels(prev => ({
      ...prev,
      [bandId]: { ...prev[bandId], open: !existing?.open }
    }));

    if (existing?.loaded || existing?.loading) return;

    setShopPanels(prev => ({ ...prev, [bandId]: { ...prev[bandId], loading: true } }));
    try {
      const result = await shopsApi.getShopsForBand(bandId);
      if (result.success) {
        setShopPanels(prev => ({ ...prev, [bandId]: { ...prev[bandId], loading: false, loaded: true, data: result.data || [] } }));
      } else {
        setShopPanels(prev => ({ ...prev, [bandId]: { ...prev[bandId], loading: false, loaded: true, error: result.error?.message || t('mangaDetail.noShopLinks') } }));
      }
    } catch (e) {
      setShopPanels(prev => ({ ...prev, [bandId]: { ...prev[bandId], loading: false, loaded: true, error: e.message } }));
    }
  };

  const handleNotifSettingChange = async (key) => {
    if (!notifSettings) return;
    const previous = notifSettings;
    const updated = { ...notifSettings, [key]: !notifSettings[key] };
    setNotifSettings(updated);
    setNotifSaving(true);
    try {
      const result = await libraryApi.updateMangaNotificationSettings(mangaId, updated);
      if (!result.success) {
        setNotifSettings(previous);
      }
    } catch (e) {
      setNotifSettings(previous);
    } finally {
      setNotifSaving(false);
    }
  };

  const handleDeleteManga = async () => {
    const confirmDelete = await confirm(t('mangaDetail.confirmDeleteManga', { title: mangaInfo?.title || mangaTitle }), { danger: true });
    if (!confirmDelete) return;

    setIsDeleting(true);
    try {
      const result = await libraryApi.removeManga(mangaId);

      if (result.success) {
        if (onMangaDeleted) onMangaDeleted(mangaId);
        onClose();
      } else {
        console.error('❌ Fehler beim Löschen:', result.error);
        alert(t('mangaDetail.errorDeletingManga', { message: result.error?.message || t('mangaDetail.unknownError') }));
      }
    } catch (error) {
      console.error('❌ Netzwerkfehler:', error);
      alert(t('mangaDetail.networkErrorDeletingManga'));
    } finally {
      setIsDeleting(false);
    }
  };

  const ownedCount = userBands.length;
  const totalCount = allBands.length;
  const progressPercent = totalCount > 0 ? (ownedCount / totalCount) * 100 : 0;
  const readCount = allBands.filter(b => b.isOwned && b.readingStatus === 'READ').length;
  const readPercent = ownedCount > 0 ? (readCount / ownedCount) * 100 : 0;

  const STATUS_LABEL_KEYS = {
    RUNNING: 'mangaDetail.statusRunning',
    ONGOING: 'mangaDetail.statusRunning',
    RELEASING: 'mangaDetail.statusRunning',
    FINISHED: 'mangaDetail.statusFinished',
    COMPLETED: 'mangaDetail.statusFinished',
    CANCELLED: 'mangaDetail.statusCancelled',
    CANCELED: 'mangaDetail.statusCancelled',
    HIATUS: 'mangaDetail.statusHiatus',
    DROPPED: 'mangaDetail.statusDropped',
    NOT_YET_RELEASED: 'mangaDetail.statusNotYetReleased',
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        {(isLoading || error) && (
          <button className={styles.closeButton} onClick={onClose} aria-label={t('mangaDetail.closeAria')}>
            ✕
          </button>
        )}

        {isLoading ? (
          <div className={styles.loading}>
            <div className={styles.spinner}></div>
            <p>{t('mangaDetail.loading')}</p>
          </div>
        ) : error ? (
          <div className={styles.error}>
            <p>{t('mangaDetail.errorPrefix', { message: error })}</p>
          </div>
        ) : (
          <>
            {/* Header mit Cover und Titel */}
            <div className={styles.header}>
              <button className={styles.backButton} onClick={onClose}>
                {t('mangaDetail.back')}
              </button>
              <h2 className={styles.modalTitle}>{mangaInfo?.title || mangaTitle}</h2>
              <button
                className={styles.notifSettingsButton}
                onClick={() => setShowNotifPanel(prev => !prev)}
                title={t('mangaDetail.notifSettingsTitle')}
              >
                🔔 {t('mangaDetail.notifSettingsBtn')}
              </button>
            </div>

            {showNotifPanel && (
              <div className={styles.notifPanel}>
                {!notifSettings ? (
                  <p className={styles.notifPanelHint}>{t('mangaDetail.notifLoading')}</p>
                ) : (
                  <>
                    <label className={styles.notifToggleRow}>
                      <span>{t('mangaDetail.notifNewBands')}</span>
                      <input
                        type="checkbox"
                        checked={notifSettings.new_release_enabled}
                        onChange={() => handleNotifSettingChange('new_release_enabled')}
                        disabled={notifSaving}
                      />
                    </label>
                    <label className={styles.notifToggleRow}>
                      <span>{t('mangaDetail.notifPopup')}</span>
                      <input
                        type="checkbox"
                        checked={notifSettings.popup_enabled}
                        onChange={() => handleNotifSettingChange('popup_enabled')}
                        disabled={notifSaving}
                      />
                    </label>
                    <label className={styles.notifToggleRow}>
                      <span>{t('mangaDetail.notifEmail')}</span>
                      <input
                        type="checkbox"
                        checked={notifSettings.email_enabled}
                        onChange={() => handleNotifSettingChange('email_enabled')}
                        disabled={notifSaving}
                      />
                    </label>
                    <label className={styles.notifToggleRow}>
                      <span>{t('mangaDetail.notifAutoWishlist')}</span>
                      <input
                        type="checkbox"
                        checked={notifSettings.auto_add_to_wishlist}
                        onChange={() => handleNotifSettingChange('auto_add_to_wishlist')}
                        disabled={notifSaving}
                      />
                    </label>
                  </>
                )}
              </div>
            )}

            {/* Cover und Manga Info */}
            <div className={styles.mangaHeader}>
              {(coverUrl || mangaInfo?.cover_media_key) && (
                <img 
                  src={getMediaUrl(mangaInfo?.cover_media_key) || coverUrl} 
                  alt={mangaInfo?.title || mangaTitle} 
                  className={styles.coverImage}
                />
              )}
              <div className={styles.mangaInfo}>
                <h1 className={styles.mangaTitle}>{mangaInfo?.title || mangaTitle}</h1>

                {wishlistItems.some(item => !item.band_id) && (
                  <span className={styles.wishlistStatusBadge}>♥ {t('mangaDetail.onWishlistBadge')}</span>
                )}

                {/* Status Badge */}
                {mangaInfo?.status && (() => {
                  const statusKey = mangaInfo.status.toUpperCase();
                  const cssKeys = {
                    RUNNING: 'running', ONGOING: 'ongoing', RELEASING: 'releasing',
                    FINISHED: 'finished', COMPLETED: 'completed',
                    CANCELLED: 'cancelled', CANCELED: 'cancelled',
                    HIATUS: 'hiatus', DROPPED: 'hiatus',
                    NOT_YET_RELEASED: 'notyetreleased',
                  };
                  const label = STATUS_LABEL_KEYS[statusKey] ? t(STATUS_LABEL_KEYS[statusKey]) : mangaInfo.status;
                  const cssClass = cssKeys[statusKey] || mangaInfo.status.toLowerCase();
                  return (
                    <span className={`${styles.statusBadge} ${styles[cssClass] || ''}`}>
                      {label}
                    </span>
                  );
                })()}

                {/* Info Grid */}
                <div className={styles.infoGrid}>
                  {mangaInfo?.publisher && (
                    <div className={styles.infoRow}>
                      <span className={styles.infoIcon}>🏢</span>
                      <span className={styles.infoLabel}>{t('mangaDetail.publisher')}</span>
                      <span className={styles.infoText}>{mangaInfo.publisher}</span>
                    </div>
                  )}
                  {mangaInfo?.band_count > 0 && (
                    <div className={styles.infoRow}>
                      <span className={styles.infoIcon}>📚</span>
                      <span className={styles.infoLabel}>{t('mangaDetail.volumes')}</span>
                      <span className={styles.infoText}>{mangaInfo.band_count}</span>
                    </div>
                  )}
                  {(mangaInfo?.average_score || mangaInfo?.mean_score) && (
                    <div className={styles.infoRow}>
                      <span className={styles.infoIcon}>⭐</span>
                      <span className={styles.infoLabel}>{t('mangaDetail.score')}</span>
                      <span className={styles.infoText}>
                        {mangaInfo.average_score || mangaInfo.mean_score}/10
                      </span>
                    </div>
                  )}
                  {mangaInfo?.rankings && (
                    <div className={styles.infoRow}>
                      <span className={styles.infoIcon}>🏆</span>
                      <span className={styles.infoLabel}>{t('mangaDetail.ranking')}</span>
                      <span className={styles.infoText}>#{mangaInfo.rankings}</span>
                    </div>
                  )}
                </div>

                {/* Genres */}
                {mangaInfo?.genres && mangaInfo.genres.length > 0 && (
                  <div className={styles.genresRow}>
                    {(Array.isArray(mangaInfo.genres) ? mangaInfo.genres : mangaInfo.genres.split(',')).map((genre, idx) => (
                      <span key={idx} className={styles.genreTag}>{genre.trim()}</span>
                    ))}
                  </div>
                )}

                {/* Description */}
                {mangaInfo?.description && (
                  <div className={styles.description}>
                    <p>{mangaInfo.description}</p>
                  </div>
                )}

                <button 
                  className={styles.deleteMangaButton}
                  onClick={handleDeleteManga}
                  disabled={isDeleting}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6" />
                  </svg>
                  {t('mangaDetail.deleteManga')}
                </button>
              </div>
            </div>

            {/* Progress Section */}
            <div className={styles.progressSection}>
              <div className={styles.progressBlock}>
                <div className={styles.progressLabelRow}>
                  <span className={styles.progressLabel}>📚 {t('mangaDetail.collectionProgress')}</span>
                  <span className={styles.progressPercent}>{Math.round(progressPercent)}%</span>
                </div>
                <div className={styles.progressBarContainer}>
                  <div className={styles.progressBarFill} style={{ width: `${progressPercent}%` }}></div>
                </div>
                <span className={styles.progressSubtext}>{t('mangaDetail.ownedOfTotal', { owned: ownedCount, total: totalCount })}</span>
              </div>

              <div className={styles.progressBlock}>
                <div className={styles.progressLabelRow}>
                  <span className={styles.progressLabel}>📖 {t('mangaDetail.readProgress')}</span>
                  <span className={styles.progressPercent}>{Math.round(readPercent)}%</span>
                </div>
                <div className={styles.progressBarContainer}>
                  <div className={`${styles.progressBarFill} ${styles.progressBarFillRead}`} style={{ width: `${readPercent}%` }}></div>
                </div>
                <span className={styles.progressSubtext}>{t('mangaDetail.readOfOwned', { read: readCount, owned: ownedCount })}</span>
              </div>
            </div>

            {/* Bände Liste */}
            <div className={styles.bandsSection}>
              <div className={styles.bandsHeaderRow}>
                <h3 className={styles.bandsTitle}>{t('mangaDetail.allVolumes')}</h3>
                <div className={styles.bulkActions}>
                  {!allBands.every(b => b.isOwned) && (
                    <button
                      type="button"
                      className={styles.bulkActionButton}
                      onClick={handleBulkAddToCollection}
                      disabled={!!bulkActionLoading}
                    >
                      {bulkActionLoading === 'collection' ? t('mangaDetail.bulkAddCollectionLoading') : t('mangaDetail.bulkAddCollectionAction')}
                    </button>
                  )}
                  {!allBands.every(b => b.isOwned || findWishlistEntry(b)) && (
                    <button
                      type="button"
                      className={styles.bulkActionButton}
                      onClick={handleBulkAddToWishlist}
                      disabled={!!bulkActionLoading}
                    >
                      {bulkActionLoading === 'wishlist' ? t('mangaDetail.bulkAddWishlistLoading') : t('mangaDetail.bulkAddWishlistAction')}
                    </button>
                  )}
                  {allBands.some(b => b.isOwned && b.readingStatus !== 'READ') && (
                    <button
                      type="button"
                      className={styles.bulkActionButton}
                      onClick={handleBulkMarkAllRead}
                      disabled={!!bulkActionLoading}
                    >
                      {bulkActionLoading === 'read' ? t('mangaDetail.bulkMarkReadLoading') : t('mangaDetail.bulkMarkReadAction')}
                    </button>
                  )}
                </div>
              </div>
              {bulkMsg && (
                <div className={`${styles.bandMsgInline} ${styles[`bandMsg_${bulkMsg.type}`]}`}>
                  <span>{bulkMsg.text}</span>
                </div>
              )}
              <div className={styles.bandsList}>
                {allBands.map((band) => (
                  <div 
                    key={band.bandNumber} 
                    className={`${styles.bandItem} ${band.isOwned ? styles.owned : styles.notOwned}`}
                  >
                    <div className={styles.bandStatus}>
                      {band.isOwned ? (
                        <span className={styles.checkmark}>✓</span>
                      ) : (
                        <span className={styles.cross}>✗</span>
                      )}
                    </div>
                    <div className={styles.bandIcon} onClick={() => toggleBandInfo(band.bandNumber)} role="button" tabIndex={0}>
                      {band.coverUrl ? (
                        <img
                          src={band.coverUrl}
                          alt={band.titel}
                          className={styles.bandCoverImage}
                          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                        />
                      ) : null}
                      <span className={styles.bookIcon} style={band.coverUrl ? { display: 'none' } : undefined}>📖</span>
                    </div>
                    <div className={styles.bandInfo} onClick={() => toggleBandInfo(band.bandNumber)} role="button" tabIndex={0}>
                      <div className={styles.bandHeader}>
                        <span className={styles.bandLabel}>{t('mangaDetail.volumeLabel', { number: band.bandNumber })}</span>
                        <svg
                          width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"
                          className={`${styles.bandExpandChevron} ${expandedBandNumber === band.bandNumber ? styles.bandExpandChevronOpen : ''}`}
                        >
                          <path d="M4 6L8 10L12 6" />
                        </svg>
                      </div>
                      <div className={styles.bandTitle}>{band.titel}</div>
                    </div>
                    <div className={styles.bandActions}>
                      {bandMsg?.bandNumber === band.bandNumber && (
                        <div className={`${styles.bandMsgInline} ${styles[`bandMsg_${bandMsg.type}`]}`}>
                          <span>{bandMsg.text}</span>
                          {bandMsg.showNav && (
                            <button className={styles.bandMsgNav} onClick={() => { onClose(); navigate('/add-volume'); }}>
                              {t('mangaDetail.addVolumeAction')}
                            </button>
                          )}
                        </div>
                      )}
                      {band.isOwned ? (
                        <>
                          <select
                            className={styles.readingStatusSelect}
                            value={band.readingStatus || 'UNREAD'}
                            onChange={(e) => handleChangeReadingStatus(band, e.target.value)}
                            title={t('mangaDetail.changeReadingStatus')}
                          >
                            <option value="UNREAD">{t('mangaDetail.statusUnread')}</option>
                            <option value="READING">{t('mangaDetail.statusReading')}</option>
                            <option value="READ">{t('mangaDetail.statusRead')}</option>
                          </select>
                          <button
                            className={styles.deleteBandButton}
                            onClick={() => handleDeleteBand(band)}
                            disabled={isDeleting}
                            title={t('mangaDetail.removeVolume')}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                            </svg>
                          </button>
                        </>
                      ) : (
                        <button 
                          className={styles.actionButtonAdd}
                          onClick={() => handleAddBand(band)}
                          disabled={isDeleting}
                          title={t('mangaDetail.addToCollectionTitle')}
                        >
                          {t('mangaDetail.iOwnIt')}
                        </button>
                      )}
                      <button
                        className={`${styles.wishlistButton} ${findWishlistEntry(band) ? styles.wishlistButtonActive : ''}`}
                        onClick={() => handleWishlistBand(band)}
                        title={findWishlistEntry(band) ? t('mangaDetail.removeFromWishlistTitle') : t('mangaDetail.addToWishlistTitle')}
                      >
                        ♥
                      </button>
                      {!band.isOwned && band.bandId != null && (
                        <button
                          className={`${styles.shopButton} ${shopPanels[band.bandId]?.open ? styles.shopButtonActive : ''}`}
                          onClick={() => handleToggleShopLinks(band)}
                          title={t('mangaDetail.showBuyOptionsTitle')}
                        >
                          🛒
                        </button>
                      )}
                    </div>
                    {!band.isOwned && band.bandId != null && shopPanels[band.bandId]?.open && (
                      <div className={styles.shopLinksPanel}>
                        {shopPanels[band.bandId].loading ? (
                          <span className={styles.shopLinksHint}>{t('mangaDetail.loadingBuyOptions')}</span>
                        ) : shopPanels[band.bandId].error ? (
                          <span className={styles.shopLinksHint}>{shopPanels[band.bandId].error}</span>
                        ) : shopPanels[band.bandId].data?.length > 0 ? (
                          shopPanels[band.bandId].data.map((shop, idx) => (
                            <a
                              key={idx}
                              href={shop.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.shopLink}
                            >
                              {shop.name}
                            </a>
                          ))
                        ) : (
                          <span className={styles.shopLinksHint}>{t('mangaDetail.noBuyOptions')}</span>
                        )}
                      </div>
                    )}
                    {expandedBandNumber === band.bandNumber && (
                      <div className={styles.bandDetailPanel}>
                        {band.author && (
                          <span className={styles.bandDetailRow}><strong>{t('mangaDetail.author')}</strong> {band.author}</span>
                        )}
                        {band.publisher && (
                          <span className={styles.bandDetailRow}>{t('mangaDetail.publisher')} {band.publisher}</span>
                        )}
                        {band.publishedDate && (
                          <span className={styles.bandDetailRow}><strong>{t('mangaDetail.publishedDate')}</strong> {new Date(band.publishedDate).toLocaleDateString()}</span>
                        )}
                        {band.language && (
                          <span className={styles.bandDetailRow}><strong>{t('mangaDetail.language')}</strong> {formatLanguage(band.language, i18n.language)}</span>
                        )}
                        {!band.author && !band.publisher && !band.publishedDate && !band.language && (
                          <span className={styles.bandDetailRow}>{t('mangaDetail.noBandInfo')}</span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default MangaDetailModal;
